import { createFileRoute } from '@tanstack/react-router'
import { type ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { GiftCard, GiftCardStatusType } from '@/api/admin-gift-card-types'
import { preloadListGiftCards, useListGiftCards } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { SearchField } from '@/components/base/search-field'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { SingleColumnPage } from '@/components/page-layout'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table, useServerSort } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { paginatedSearchWithSort } from '@/utils/route-utilities'
export const Route = createFileRoute('/_authenticated/_general/gift-cards/')({
	component: GiftCardsPage,
	validateSearch: paginatedSearchWithSort(['name', 'code', 'status']),
	loaderDeps: ({ search: { limit, offset, q, sortBy, sortOrder } }) => ({
		limit,
		offset,
		q,
		sortBy,
		sortOrder
	}),
	loader: async ({ context, deps: { limit, offset, q, sortBy, sortOrder } }) =>
		await preloadListGiftCards(context, {
			query: {
				limit,
				offset,
				q,
				sortBy,
				sortOrder
			}
		}),
	staticData: {
		crumb: ({ context: { L } }) => L.GiftCards.Title()
	}
})

function GiftCardsPage() {
	const { L } = useI18n()
	const { limit = DEFAULT_PAGE_SIZE, offset, q, sortBy, sortOrder } = Route.useSearch()
	const serverSort = useServerSort(['name', 'code', 'status'])

	const giftCards = useListGiftCards({
		limit,
		offset,
		q,
		sortBy,
		sortOrder
	})

	const columnDefs = useMemo<ColDef<GiftCard>[]>(
		() => [
			{
				headerName: L.GiftCards.Column.Name(),
				field: 'name',
				minWidth: 150,
				flex: 2
			},
			{
				headerName: L.GiftCards.Column.Code(),
				field: 'code',
				minWidth: 150,
				flex: 2
			},
			{
				headerName: L.GiftCards.Column.Amount(),
				field: 'amount',
				minWidth: 120,
				flex: 1,
				valueFormatter: ({ data }) =>
					data
						? L.Amount.Default({
								amount: data.amount,
								currencyCode: data.currencyCode
							})
						: ''
			},
			{
				headerName: L.GiftCards.Column.Status(),
				field: 'status',
				minWidth: 100,
				flex: 1,
				cellRenderer: ({ value }: { value: GiftCardStatusType }) => (
					<StatusCell color={getGiftCardStatusColor(value)}>{L.Enum.GiftCardStatus(value)}</StatusCell>
				)
			}
		],
		[L]
	)

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader heading={L.GiftCards.Title()} rightSide={<SearchField />} />
				<div className='min-w-0 flex-1 overflow-x-auto'>
					<Table<GiftCard>
						className='w-full min-w-[600px] flex-1'
						data={giftCards.data?.giftCards}
						columnUtilities={{}}
						columnDefs={columnDefs}
						serverSort={serverSort}
						animateRows
						suppressCellFocus
						rowToRouterConfig={giftCard => ({
							to: '/gift-cards/$giftCardUuid',
							params: { giftCardUuid: giftCard.uuid }
						})}
					/>
				</div>
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={giftCards.data?.count} />
				</div>
			</Container>
		</SingleColumnPage>
	)
}

const getGiftCardStatusColor = (status: GiftCardStatusType): StatusColor => {
	switch (status) {
		case 'ACTIVE':
			return 'green'
		case 'RESERVED':
			return 'blue'
		case 'REDEEMED':
			return 'grey'
		default:
			return 'grey'
	}
}
